/// <reference path="../../lib/jQuery.d.ts" />

import { Utils } from "../core/utils";
import { Floorplan } from "../model/floorplan";
import { Wall } from "../model/wall";
import { FloorplannerView, floorplannerModes } from "./floorplanner_view";

/** how much will we move a corner to make a wall axis aligned (cm) */
const snapTolerance = 25;
const minWallHeight = 90;
const maxWallHeight = 1000;
const wallHeightHandleOffset = 35;
const wallHeightSpinnerOffsetPx = 24;
const wallHeightSpinnerWidthPx = 22;
const wallHeightButtonHeightPx = 12;
const wallHeightStepCm = 2.54;

/**
 * The Floorplanner implements an interactive tool for creation of floorplans.
 */
export class Floorplanner {

  /** */
  public mode = 0;

  /** */
  public activeWall = null;

  /** */
  public activeCorner = null;

  /** */
  public selectedWall: Wall = null;

  /** */
  public isWallHeightDragging = false;

  /** */
  public wallHeightHoveredArrow: string = null;

  /** */
  public wallHeightGuardMessage: string = null;

  /** */
  public originX = 0;

  /** */
  public originY = 0;

  /** drawing state */
  public targetX = 0;

  /** drawing state */
  public targetY = 0;

  /** drawing state */
  public lastNode = null;

  /** */
  private wallWidth: number;

  /** */
  private modeResetCallbacks = $.Callbacks();

  /** */
  private canvasElement;

  /** */
  private view: FloorplannerView;

  /** */
  private mouseDown = false;

  /** */
  private mouseMoved = false;

  /** in ThreeJS coords */
  private mouseX = 0;

  /** in ThreeJS coords */
  private mouseY = 0;

  /** in ThreeJS coords */
  private rawMouseX = 0;

  /** in ThreeJS coords */
  private rawMouseY = 0;

  /** mouse position at last click */
  private lastX = 0;

  /** mouse position at last click */
  private lastY = 0;

  /** */
  private cmPerPixel: number;

  /** */
  private pixelsPerCm: number;

  /** */
  private wallHeightGuardMessageUntil = 0;

  /** */
  constructor(canvas: string, private floorplan: Floorplan) {

    this.canvasElement = $("#" + canvas);

    this.view = new FloorplannerView(this.floorplan, this, canvas);

    var cmPerFoot = 30.48;
    var pixelsPerFoot = 15.0;
    this.cmPerPixel = cmPerFoot * (1.0 / pixelsPerFoot);
    this.pixelsPerCm = 1.0 / this.cmPerPixel;

    this.wallWidth = 10.0 * this.pixelsPerCm;

    // Initialization:

    this.setMode(floorplannerModes.MOVE);

    var scope = this;

    this.canvasElement.mousedown((event) => {
      scope.mousedown(event);
    });
    this.canvasElement.mousemove((event) => {
      scope.mousemove(event);
    });
    this.canvasElement.mouseup(() => {
      scope.mouseup();
    });
    this.canvasElement.mouseleave(() => {
      scope.mouseleave();
    });

    $(document).keyup((e) => {
      if (e.keyCode == 27) {
        scope.escapeKey();
      }
    });

    floorplan.roomLoadedCallbacks.add(() => {
      scope.reset()
    });
  }

  /** */
  private escapeKey() {
    this.setMode(floorplannerModes.MOVE);
  }

  /** */
  private updateMousePosition(event) {
    this.rawMouseX = event.clientX;
    this.rawMouseY = event.clientY;

    this.mouseX = (event.clientX - this.canvasElement.offset().left) * this.cmPerPixel + this.originX * this.cmPerPixel;
    this.mouseY = (event.clientY - this.canvasElement.offset().top) * this.cmPerPixel + this.originY * this.cmPerPixel;
  }

  private getMouseCanvasPosition(event): { x: number, y: number } {
    return {
      x: event.clientX - this.canvasElement.offset().left,
      y: event.clientY - this.canvasElement.offset().top
    };
  }

  /** */
  private updateTarget() {
    if (this.mode == floorplannerModes.DRAW && this.lastNode) {
      if (Math.abs(this.mouseX - this.lastNode.x) < snapTolerance) {
        this.targetX = this.lastNode.x;
      } else {
        this.targetX = this.mouseX;
      }
      if (Math.abs(this.mouseY - this.lastNode.y) < snapTolerance) {
        this.targetY = this.lastNode.y;
      } else {
        this.targetY = this.mouseY;
      }
    } else {
      this.targetX = this.mouseX;
      this.targetY = this.mouseY;
    }

    this.view.draw();
  }

  private getWallHeightMinimum(wall: Wall): number {
    var openingMinimum = wall ? wall.getMinimumHeightForDoorWindow() : 0;
    return Math.max(minWallHeight, openingMinimum);
  }

  private clampWallHeight(height: number, wall: Wall) {
    return Math.max(this.getWallHeightMinimum(wall), Math.min(maxWallHeight, height));
  }

  private showWallHeightGuardMessage(message: string) {
    this.wallHeightGuardMessage = message;
    var scope = this;
    var expiresAt = Date.now() + 2200;
    this.wallHeightGuardMessageUntil = expiresAt;
    this.view.draw();
    setTimeout(() => {
      if (scope.wallHeightGuardMessageUntil === expiresAt) {
        scope.wallHeightGuardMessage = null;
        scope.view.draw();
      }
    }, 2250);
  }

  public getWallHeightGuardMessage(): string {
    if (this.wallHeightGuardMessage && Date.now() > this.wallHeightGuardMessageUntil) {
      this.wallHeightGuardMessage = null;
    }
    return this.wallHeightGuardMessage;
  }

  private adjustWallHeight(wall: Wall, deltaCm: number) {
    if (!wall) {
      return;
    }

    var requestedHeight = wall.height + deltaCm;
    var minAllowed = this.getWallHeightMinimum(wall);
    var nextHeight = this.clampWallHeight(requestedHeight, wall);
    var blockedByOpening = deltaCm < 0 && requestedHeight < minAllowed && minAllowed > minWallHeight + 0.001;

    if (Math.abs(nextHeight - wall.height) < 0.001) {
      if (blockedByOpening) {
        this.showWallHeightGuardMessage("Can't lower further: door/window on this wall.");
      }
      return;
    }

    wall.setWallHeight(nextHeight);
    this.floorplan.update();
    this.view.draw();

    if (blockedByOpening) {
      this.showWallHeightGuardMessage("Can't lower further: door/window on this wall.");
    }
  }

  public getWallHeightControlPoint(wall: Wall): { x: number, y: number, wallMidX: number, wallMidY: number } {
    if (!wall) {
      return null;
    }

    var startX = wall.getStartX();
    var startY = wall.getStartY();
    var endX = wall.getEndX();
    var endY = wall.getEndY();

    var dx = endX - startX;
    var dy = endY - startY;
    var length = Utils.distance(startX, startY, endX, endY);
    if (length < 0.001) {
      return null;
    }

    var midX = (startX + endX) / 2.0;
    var midY = (startY + endY) / 2.0;

    var normalX = -dy / length;
    var normalY = dx / length;

    return {
      x: midX + normalX * wallHeightHandleOffset,
      y: midY + normalY * wallHeightHandleOffset,
      wallMidX: midX,
      wallMidY: midY
    };
  }

  public getWallHeightSpinnerUi(wall: Wall) {
    var control = this.getWallHeightControlPoint(wall);
    if (!control) {
      return null;
    }

    var wallMidX = this.convertX(control.wallMidX);
    var wallMidY = this.convertY(control.wallMidY);
    var handleX = this.convertX(control.x);
    var handleY = this.convertY(control.y);

    var directionX = handleX - wallMidX;
    var directionY = handleY - wallMidY;
    var directionLength = Math.sqrt(directionX * directionX + directionY * directionY) || 1;

    var centerX = handleX + (directionX / directionLength) * wallHeightSpinnerOffsetPx;
    var centerY = handleY + (directionY / directionLength) * wallHeightSpinnerOffsetPx;

    var width = wallHeightSpinnerWidthPx;
    var buttonHeight = wallHeightButtonHeightPx;
    var totalHeight = buttonHeight * 2;

    return {
      wallMidX: wallMidX,
      wallMidY: wallMidY,
      centerX: centerX,
      centerY: centerY,
      width: width,
      buttonHeight: buttonHeight,
      totalHeight: totalHeight,
      labelX: centerX,
      labelY: centerY - (totalHeight / 2) - 8,
      upRect: {
        left: centerX - (width / 2),
        right: centerX + (width / 2),
        top: centerY - (totalHeight / 2),
        bottom: centerY
      },
      downRect: {
        left: centerX - (width / 2),
        right: centerX + (width / 2),
        top: centerY,
        bottom: centerY + (totalHeight / 2)
      }
    };
  }

  private pointInRect(x: number, y: number, rect): boolean {
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  }

  private getWallHeightArrowAtPosition(wall: Wall, canvasX: number, canvasY: number): string {
    var ui = this.getWallHeightSpinnerUi(wall);
    if (!ui) {
      return null;
    }
    if (this.pointInRect(canvasX, canvasY, ui.upRect)) {
      return 'up';
    }
    if (this.pointInRect(canvasX, canvasY, ui.downRect)) {
      return 'down';
    }
    return null;
  }

  /** */
  private mousedown(event) {
    this.updateMousePosition(event);
    var clickedCorner = this.floorplan.overlappedCorner(this.mouseX, this.mouseY);
    var clickedWall = clickedCorner ? null : this.floorplan.overlappedWall(this.mouseX, this.mouseY, 20);
    this.activeCorner = clickedCorner;
    this.activeWall = clickedWall;

    this.mouseDown = true;
    this.mouseMoved = false;
    this.lastX = this.rawMouseX;
    this.lastY = this.rawMouseY;

    if (this.mode == floorplannerModes.MOVE) {
      var previousSelectedWall = this.selectedWall;
      var mouseCanvasPos = this.getMouseCanvasPosition(event);
      var arrowFromPrevious = previousSelectedWall
        ? this.getWallHeightArrowAtPosition(previousSelectedWall, mouseCanvasPos.x, mouseCanvasPos.y)
        : null;

      if (arrowFromPrevious) {
        this.selectedWall = previousSelectedWall;
        this.activeWall = previousSelectedWall;
        if (arrowFromPrevious === 'up') {
          this.adjustWallHeight(this.selectedWall, wallHeightStepCm);
        } else {
          this.adjustWallHeight(this.selectedWall, -wallHeightStepCm);
        }
        this.mouseDown = false;
        return;
      }

      if (clickedWall) {
        this.selectedWall = clickedWall;
      } else if (!clickedCorner) {
        this.selectedWall = null;
      }

      if (previousSelectedWall !== this.selectedWall) {
        this.view.draw();
      }
    }

    // delete
    if (this.mode == floorplannerModes.DELETE) {
      this.selectedWall = null;
      if (this.activeCorner) {
        this.activeCorner.removeAll();
      } else if (this.activeWall) {
        this.activeWall.remove();
      } else {
        this.setMode(floorplannerModes.MOVE);
      }
    }
  }

  /** */
  private mousemove(event) {
    this.mouseMoved = true;

    this.updateMousePosition(event);

    // update target (snapped position of actual mouse)
    if (this.mode == floorplannerModes.DRAW || (this.mode == floorplannerModes.MOVE && this.mouseDown)) {
      this.updateTarget();
    }

    // update object target
    if (this.mode != floorplannerModes.DRAW && !this.mouseDown) {
      var hoverCorner = this.floorplan.overlappedCorner(this.mouseX, this.mouseY);
      var hoverWall = this.floorplan.overlappedWall(this.mouseX, this.mouseY);
      var draw = false;
      if (hoverCorner != this.activeCorner) {
        this.activeCorner = hoverCorner;
        draw = true;
      }
      // corner takes precendence
      if (this.activeCorner == null) {
        if (hoverWall != this.activeWall) {
          this.activeWall = hoverWall;
          draw = true;
        }
      } else {
        this.activeWall = null;
      }

      var wallForControls = this.selectedWall || this.activeWall;
      var hoverArrow = null;
      if (wallForControls) {
        var mouseCanvasPos = this.getMouseCanvasPosition(event);
        hoverArrow = this.getWallHeightArrowAtPosition(wallForControls, mouseCanvasPos.x, mouseCanvasPos.y);
      }
      if (hoverArrow !== this.wallHeightHoveredArrow) {
        this.wallHeightHoveredArrow = hoverArrow;
        draw = true;
      }

      if (draw) {
        this.view.draw();
      }
    }

    // panning
    if (this.mouseDown && !this.activeCorner && !this.activeWall) {
      this.originX += (this.lastX - this.rawMouseX);
      this.originY += (this.lastY - this.rawMouseY);
      this.lastX = this.rawMouseX;
      this.lastY = this.rawMouseY;
      this.view.draw();
    }

    // dragging
    if (this.mode == floorplannerModes.MOVE && this.mouseDown) {
      if (this.activeCorner) {
        this.activeCorner.move(this.mouseX, this.mouseY);
        this.activeCorner.snapToAxis(snapTolerance);
      } else if (this.activeWall) {
        this.selectedWall = this.activeWall;
        this.activeWall.relativeMove(
          (this.rawMouseX - this.lastX) * this.cmPerPixel,
          (this.rawMouseY - this.lastY) * this.cmPerPixel
        );
        this.activeWall.snapToAxis(snapTolerance);
        this.lastX = this.rawMouseX;
        this.lastY = this.rawMouseY;
      }
      this.view.draw();
    }
  }

  /** */
  private mouseup() {
    this.mouseDown = false;

    // drawing
    if (this.mode == floorplannerModes.DRAW && !this.mouseMoved) {
      var corner = this.floorplan.newCorner(this.targetX, this.targetY);
      if (this.lastNode != null) {
        this.floorplan.newWall(this.lastNode, corner);
      }
      if (corner.mergeWithIntersected() && this.lastNode != null) {
        this.setMode(floorplannerModes.MOVE);
      }
      this.lastNode = corner;
    }
  }

  /** */
  private mouseleave() {
    this.mouseDown = false;
    if (this.wallHeightHoveredArrow != null) {
      this.wallHeightHoveredArrow = null;
      this.view.draw();
    }
    //scope.setMode(scope.modes.MOVE);
  }

  /** */
  private reset() {
    this.selectedWall = null;
    this.wallHeightHoveredArrow = null;
    this.wallHeightGuardMessage = null;
    this.resizeView();
    this.setMode(floorplannerModes.MOVE);
    this.resetOrigin();
    this.view.draw();
  }

  /** */
  private resizeView() {
    this.view.handleWindowResize();
  }

  /** */
  private setMode(mode: number) {
    this.lastNode = null;
    this.mode = mode;
    if (mode != floorplannerModes.MOVE) {
      this.selectedWall = null;
      this.wallHeightHoveredArrow = null;
      this.wallHeightGuardMessage = null;
    }
    this.modeResetCallbacks.fire(mode);
    this.updateTarget();
  }

  /** Sets the origin so that floorplan is centered */
  private resetOrigin() {
    var centerX = this.canvasElement.innerWidth() / 2.0;
    var centerY = this.canvasElement.innerHeight() / 2.0;
    var centerFloorplan = this.floorplan.getCenter();
    this.originX = centerFloorplan.x * this.pixelsPerCm - centerX;
    this.originY = centerFloorplan.z * this.pixelsPerCm - centerY;
  }

  /** Convert from THREEjs coords to canvas coords. */
  public convertX(x: number): number {
    return (x - this.originX * this.cmPerPixel) * this.pixelsPerCm;
  }

  /** Convert from THREEjs coords to canvas coords. */
  public convertY(y: number): number {
    return (y - this.originY * this.cmPerPixel) * this.pixelsPerCm;
  }
}

export { floorplannerModes as floorplannerModes };
